import { farmPayout } from "@highmarch/app";
import { BalanceData, ChestBand, RARITY_IDS, Rarity, bandWeightsAt, battleChestBand, battleChestBump } from "@highmarch/domain";
import { closeBtnHtml, ribbonHtml } from "../kit";
import { wellHtml } from "../components";
import { chestIconUrl, resourceIconUrl } from "../portraits";
import { SHOP_SKIN } from "../shopSkin";

/**
 * The battle card's prize chest (UI pass 2026-10-21): the chest a first clear (or, on a cleared level, a farm run) rolls,
 * shown on the colour of the best rarity it can roll at that level, and in focus on a tap — the chest large and the
 * odds of every rarity. The odds are the sim's own: `battleChestBand` at the level's rarity climb (`bandWeightsAt`),
 * raised by the late first clears' rarity bump (`battleChestBump`).
 */

export interface PrizeView {
  /** Percent of each rarity (index = Rarity), rounded to one decimal. */
  readonly percent: readonly number[];
  /** The best rarity the chest can roll, and its id ("legendary"). */
  readonly top: Rarity;
  readonly topId: string;
}

export function prizeView(B: BalanceData, level: number, cleared: boolean): PrizeView {
  const band = cleared ? ChestBand.Farm : battleChestBand(B, level, true, 0, true);
  const w = bandWeightsAt(B, band, level);
  const bump = cleared ? 0 : battleChestBump(B, level, true, true);
  const shifted = w.map(() => 0);
  w.forEach((x, r) => { shifted[Math.min(r + bump, shifted.length - 1)] += x; });
  const total = shifted.reduce((a, b) => a + b, 0) || 1;
  const percent = shifted.map((x) => (x <= 0 ? 0 : Math.max(0.1, Math.round((x * 1000) / total) / 10)));
  // the chest wears its most likely rarity (owner rule: never oversell a roll; the odds are one tap away)
  let top = 0;
  shifted.forEach((x, r) => { if (x >= shifted[top]) top = r; });
  return { percent, top: top as Rarity, topId: RARITY_IDS[top] ?? "common" };
}

const fmtPct = (p: number): string => `${Number.isInteger(p) ? p : p.toFixed(1)}%`;

/** The focus card: the chest large on its rarity, then one bar per rarity it can roll; a farm chest adds its gold. */
export function showPrize(parent: HTMLElement, B: BalanceData, level: number, cleared: boolean): void {
  const v = prizeView(B, level, cleared);
  const bars = v.percent.map((p, r) => (p > 0 ? `<span class="orow2"><b class="ri-${RARITY_IDS[r]}">${RARITY_IDS[r]}</b><span class="obar"><i style="width:${Math.max(2, p)}%" class="ri-${RARITY_IDS[r]}"></i></span><em>${fmtPct(p)}</em></span>` : "")).join("");
  const pay = cleared ? farmPayout(B, level) : null;
  const gold = pay ? `<p class="ifdet"><img decoding="sync" src="${resourceIconUrl("gold")}" alt="" width="20" height="20"> ~${pay.gold.toLocaleString("en-US")} · 1${pay.extraGearPercent > 0 ? "–2" : ""} gear</p>` : "";
  const scrim = document.createElement("div");
  scrim.className = `scrim dchooser ${SHOP_SKIN} odds focus prizefocus`;
  const title = cleared ? "FARM CHEST" : "FIRST CLEAR CHEST";
  scrim.innerHTML = `<div class="panel dialog dchoose" role="dialog" aria-label="${title}">${ribbonHtml(title, "", "dtitle disp")}${closeBtnHtml()}
    <div class="crows"><div class="ifocus">${wellHtml({ art: chestIconUrl(v.topId), rarity: v.top, size: "lg", tag: "span", className: "ifwell" })}${gold}
    <div class="dodd2"><span class="obars">${bars}</span></div></div></div></div>`;
  const done = (): void => scrim.remove();
  scrim.querySelector<HTMLButtonElement>(".close")!.onclick = done;
  scrim.onclick = (e) => { if (e.target === scrim) done(); };
  parent.append(scrim);
}
